import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin, ArrowRight, Star, Facebook, Instagram } from "lucide-react";
import { nav, school, logo, googleReviews } from "../mock/mock";
import CampaignStrip from "./CampaignStrip";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <CampaignStrip />
      {/* Top info bar */}
      <div className={`hidden md:block bg-[#0B2545] text-slate-300 text-[13px] overflow-hidden transition-all duration-300 ${scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"}`}>
        <div className="max-w-7xl mx-auto px-5 h-9 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:+46182650646" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-phone"><Phone size={13} className="text-[#ffce3a]" /> 018-265 06 46</a>
            <a href={`mailto:${school.email}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-email"><Mail size={13} className="text-[#ffce3a]" /> {school.email}</a>
            <span className="hidden lg:inline-flex items-center gap-1.5"><MapPin size={13} className="text-[#ffce3a]" /> {school.address}, {school.area}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={googleReviews.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-rating">
              <Star size={13} className="text-[#ffce3a] fill-[#ffce3a]" /> <span className="font-bold text-white">{googleReviews.rating}</span> · {googleReviews.count} omdömen på Google
            </a>
            <span className="flex items-center gap-3 border-l border-white/15 pl-5">
              <a href={school.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-white transition-colors"><Facebook size={14} /></a>
              <a href={school.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition-colors"><Instagram size={14} /></a>
            </span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white/90 backdrop-blur-sm border-b border-slate-100"}`}>
        <div className={`max-w-7xl mx-auto px-5 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-[4.75rem]" : "h-20 md:h-24"}`}>
          <Link to="/" className="flex items-center group" data-testid="navbar-logo-link">
            <img src={logo} alt="City Trafikskola Uppsala" className={`w-auto transition-all duration-300 group-hover:scale-[1.03] ${scrolled ? "h-[3.75rem]" : "h-16 md:h-[5.5rem]"}`} data-testid="navbar-logo" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2.5 text-[15px] font-semibold transition-colors group ${active ? "text-[#0B2545]" : "text-slate-600 hover:text-[#0B2545]"}`}
                >
                  {item.label}
                  <span className={`absolute left-4 right-4 -bottom-0.5 h-[3px] rounded-full bg-[#ffce3a] origin-left transition-transform duration-300 ${active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={school.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-book-btn"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#ffce3a] text-[#0B2545] font-bold text-[15px] hover:bg-[#ffd85f] hover:shadow-lg hover:shadow-[#ffce3a]/30 transition-all"
            >
              Boka online
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          <button className="lg:hidden text-[#0B2545] p-2 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setOpen(!open)} aria-label="Meny" data-testid="navbar-menu-btn">
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-5 py-5 shadow-2xl" data-testid="navbar-mobile-menu">
          <nav className="flex flex-col gap-1">
            {nav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-3.5 rounded-xl font-semibold text-base flex items-center justify-between ${location.pathname === item.path ? "text-[#0B2545] bg-[#fff8e1]" : "text-slate-700 hover:bg-slate-50"}`}
              >
                {item.label}
                {location.pathname === item.path && <span className="w-2 h-2 rounded-full bg-[#ffce3a]" />}
              </Link>
            ))}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a href="tel:+46182650646" className="px-4 py-3 rounded-xl bg-[#f0f5fb] text-[#0B2545] font-semibold text-sm flex items-center justify-center gap-2"><Phone size={16} /> Ring oss</a>
              <a href={`mailto:${school.email}`} className="px-4 py-3 rounded-xl bg-[#f0f5fb] text-[#0B2545] font-semibold text-sm flex items-center justify-center gap-2"><Mail size={16} /> Mejla</a>
            </div>
            <a href={school.bookingUrl} target="_blank" rel="noopener noreferrer" className="mt-2 px-4 py-4 rounded-xl bg-[#ffce3a] text-[#0B2545] font-bold text-center text-base flex items-center justify-center gap-2">
              Boka online <ArrowRight size={18} />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
